// Audit (SM-755): the append-only operator audit (a database trigger
// refuses UPDATE / DELETE), filterable, with expandable before / after,
// plus the reconciliation exports by Asia/Bangkok calendar day.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { AuditTable } from "@/components/domain/AuditTable";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiUrl } from "@/lib/api";
import { bangkokToday, localToRfc3339, rfc3339ToLocal } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";

const FIELDS = ["subject_kind", "subject_id", "actor", "action", "from", "to"] as const;
type AuditSearch = Partial<Record<(typeof FIELDS)[number], string>>;

export const Route = createFileRoute("/_app/audit")({
  validateSearch: (s: Record<string, unknown>): AuditSearch => {
    const out: AuditSearch = {};
    for (const f of FIELDS) if (typeof s[f] === "string" && s[f]) out[f] = s[f] as string;
    return out;
  },
  component: AuditPage,
});

const SUBJECT_KINDS = ["user", "client", "clock", "fault_rule", "scenario", "transaction"];
const EXPORTS = [
  ["transactions.csv", "Transactions", "§16 reconciliation: every money operation received that day"],
  ["requests.csv", "Requests", "The request log with each attempt's correlated order"],
  ["observations.csv", "Observations", "Per-order / no-request evidence, empty answers included"],
  ["audit.csv", "Audit", "Every operator action"],
] as const;

function AuditPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/audit" });
  const [draft, setDraft] = useState<AuditSearch>(search);
  const [day, setDay] = useState(bangkokToday());
  const audit = useQuery({ queryKey: keys.audit(search), queryFn: () => fetchers.audit(search) });
  const set = (k: keyof AuditSearch) => (v: string) => setDraft((d) => ({ ...d, [k]: v || undefined }));
  const dayOk = /^\d{4}-\d{2}-\d{2}$/.test(day);

  return (
    <div className="space-y-5">
      <PageHeader title="Audit" subtitle="Append-only: every console mutation writes one event in the same transaction as its effect." />

      <SectionCard title="Exports (Asia/Bangkok day)">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Day" hint="00:00–24:00 Asia/Bangkok">
            <TextInput type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="export-links">
          {EXPORTS.map(([kind, label, help]) => (
            <a
              key={kind}
              className={`card block no-underline ${dayOk ? "" : "pointer-events-none opacity-50"}`}
              href={dayOk ? apiUrl(`/export/${kind}?day=${encodeURIComponent(day)}`) : undefined}
              download
            >
              <div className="text-sm font-semibold text-t-1">{label}</div>
              <div className="mono mt-0.5 break-all text-2xs text-[var(--neon)]">/mock-admin/export/{kind}?day={day}</div>
              <div className="mt-1 text-xs t-mute">{help}</div>
            </a>
          ))}
        </div>
      </SectionCard>

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          const clean: AuditSearch = {};
          for (const f of FIELDS) if (draft[f]) clean[f] = draft[f]!.trim();
          navigate({ search: clean });
        }}
      >
        <div className="form-grid">
          <Field label="Subject kind">
            <Select value={draft.subject_kind ?? ""} onChange={(e) => set("subject_kind")(e.target.value)}>
              <option value="">Any</option>
              {SUBJECT_KINDS.map((k) => (
                <option key={k}>{k}</option>
              ))}
            </Select>
          </Field>
          <Field label="Subject id">
            <TextInput value={draft.subject_id ?? ""} onChange={(e) => set("subject_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="Actor">
            <TextInput value={draft.actor ?? ""} onChange={(e) => set("actor")(e.target.value)} />
          </Field>
          <Field label="Action" hint="e.g. BALANCE_TOP_UP, FAULT_ARMED">
            <TextInput value={draft.action ?? ""} onChange={(e) => set("action")(e.target.value)} className="mono" />
          </Field>
          <Field label="From">
            <TextInput type="datetime-local" step="1" value={rfc3339ToLocal(draft.from)} onChange={(e) => set("from")(localToRfc3339(e.target.value) ?? "")} />
          </Field>
          <Field label="Before">
            <TextInput type="datetime-local" step="1" value={rfc3339ToLocal(draft.to)} onChange={(e) => set("to")(localToRfc3339(e.target.value) ?? "")} />
          </Field>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft({});
              navigate({ search: {} });
            }}
          >
            Clear
          </Button>
          <Button type="submit">Filter</Button>
        </div>
      </form>

      <ErrorBanner error={audit.error} />
      {audit.isLoading ? <Skeleton lines={6} /> : <AuditTable rows={audit.data ?? []} emptyText="No audit events match." />}
    </div>
  );
}
